// React
import { ReactElement, useEffect, useState } from 'react';
// Next
import { useTranslation } from 'next-i18next';
// Features
import { Information } from './Information';
import { ModalOrganizerInformation } from './modals/ModalOrganizerInformation';
// Contexts
import { useDashboard } from '@utmb/contexts/DashboardContext';
// Types
import { MainLanguagesEnum } from '@utmb/types/main-languages-enum';
// Styles
import styles from './basic-information.module.scss';
import { useCountry } from '@utmb/contexts/CountryContext';
import { Font } from '@utmb/design-system/text/font';
import { BlockDashboardWrapper } from '../my-utmb/BlockDashboardWrapper';

export const OrganizerInformation = (): ReactElement | null => {
    const { dashboardOrganizer } = useDashboard();
    const { t } = useTranslation();

    const [isInformationModalOpen, setIsInformationModalOpen] = useState(false);

    const { countries, initCountries } = useCountry();

    useEffect(() => {
        initCountries();
    }, []);

    if (!dashboardOrganizer) {
        return null;
    }

    const renderOptInPreferences = () => {
        const [optInWs, optInLive] = [dashboardOrganizer?.optinWorldSeries ?? false, dashboardOrganizer?.optinUtmbLive ?? false];

        if (!optInWs && !optInLive) {
            return t('myutmb:myUtmb.myInformation.accountSettings.haveNotOptIn');
        }

        const preferences = [];
        if (optInWs) preferences.push(t('myutmb:myUtmb.myInformation.accountSettings.optInWsLabel'));
        if (optInLive) preferences.push(t('myutmb:myUtmb.myInformation.accountSettings.optInLiveLabel'));

        return preferences.join(', ');
    };

    const showOtherLanguage = !(dashboardOrganizer.preferredLanguage in MainLanguagesEnum);

    const currentCountry = countries?.find((country) => country.code === dashboardOrganizer?.countryCode);

    return (
        <div className={styles.root}>
            <BlockDashboardWrapper
                label={<Font.FuturaHeavy as="span">{t('myutmb:myUtmb.myInformation.organizerInformation.title')}</Font.FuturaHeavy>}
                onChange={() => setIsInformationModalOpen(true)}
                standalone
            >
                <div className={styles.information_container_orga}>
                    <div className={`${styles.information_row_orga} row`}>
                        <div className={`${styles.information_elem_orga} row`}>
                            <Information label={t('myutmb:myUtmb.registration.organizer-name')} value={dashboardOrganizer.name} />
                        </div>
                        <div className={`${styles.information_elem_orga} row`}>
                            <Information label={t('myutmb:myUtmb.registration.city')} value={dashboardOrganizer.city || '-'} />
                        </div>
                        <div className={`${styles.information_elem_orga} row`}>
                            <Information label={t('myutmb:myUtmb.myInformation.contact.phone')} value={dashboardOrganizer.phone || '-'} />
                        </div>
                        <div className={`${styles.information_elem_orga} row`}>
                            <Information label={t('myutmb:myUtmb.myInformation.contact.country')} value={currentCountry?.name || '-'} />
                        </div>
                        <div className={`${styles.information_elem_orga} row`}>
                            <Information
                                label={t('myutmb:myUtmb.myInformation.basicInformation.communicationLanguage')}
                                value={dashboardOrganizer.preferredLanguage ? t(`common.languages.${dashboardOrganizer.preferredLanguage.toLowerCase()}`) : '-'}
                            />
                        </div>
                        {showOtherLanguage && (
                            <div className={`${styles.information_elem_orga} row`}>
                                <Information
                                    label={t('myutmb:myUtmb.myInformation.basicInformation.otherSpokenLanguage')}
                                    value={
                                        dashboardOrganizer.fallbackLanguage ? t(`common.languages.${dashboardOrganizer.fallbackLanguage.toLowerCase()}`) : '-'
                                    }
                                />
                            </div>
                        )}
                    </div>
                    <div className={`${styles.information_row_orga} row`}>
                        <div className={`${styles.information_elem_orga} row`}>
                            <Information label={t('myutmb:myUtmb.myInformation.accountSettings.optIn')}>{renderOptInPreferences()}</Information>
                        </div>
                    </div>
                </div>
            </BlockDashboardWrapper>
            <ModalOrganizerInformation isOpen={isInformationModalOpen} close={setIsInformationModalOpen} />
        </div>
    );
};
